const axios = require('axios').default;
const { trackEvent } = require('@/utils/tracking');

// Use same-origin for API calls - Vue dev server proxy handles /api routing
const host = location.origin;

const DEFAULT_API_TIMEOUT_MS = 30000;
const parsedTimeout = Number(process.env.VUE_APP_API_TIMEOUT_MS);
const DEFAULT_TIMEOUT_MS = Number.isFinite(parsedTimeout) && parsedTimeout > 0 ? parsedTimeout : DEFAULT_API_TIMEOUT_MS;

const instance = axios.create({
  baseURL: `${host}/api/v1`,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    // requires Cache-Control for IE11 GET requests, otherwise they are not fetched again
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
  withCredentials: true,
});
instance.defaults.cache = false;

// handle error cases and extract (if possible) body.message from response and use it as error message
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      if (error.response.data && error.response.data.message) {
        error.message = error.response.data.message;
      }
    }
    throw error;
  }
);

// multipart/form-data
const instanceMultipart = axios.create({
  baseURL: `${host}/api/v1`,
  timeout: DEFAULT_TIMEOUT_MS,
  withCredentials: true,
  crossDomain: true,
});

// instance dedicated to calling the TypeScript API gateway
const instanceApiGateway = axios.create({
  baseURL: `${host}/api/v1`,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    // requires Cache-Control for IE11 GET requests, otherwise they are not fetched again
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
  withCredentials: true,
});
instanceApiGateway.defaults.cache = false;
instanceApiGateway.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      if (error.response.data && error.response.data.message) {
        error.message = error.response.data.message;
      }
    }
    throw error;
  }
);

import isAssetMgmt from '@/components/is/assetMgmt/apiCalls';
import isFm from '@/components/is/fm/apiCalls';
import digitalTwin from './apiService/digitalTwin';
import virtualDevice from './apiService/virtualDevice';
import consumers from './apiService/consumers';
import virtualDeviceDrivers from './apiService/virtualDeviceDrivers';
import dashboards from './apiService/dashboards';
import digitalTwinTemplates from '@/services/apiService/digitalTwinTemplates';

const normalizeBridgeType = (type) => {
  if (typeof type !== 'string') {
    return type;
  }
  return type.replace(/^bridge-/i, 'bridge-');
};

const normalizeBridgeConfig = (bridgeConfig) => {
  if (!bridgeConfig || typeof bridgeConfig !== 'object') {
    return bridgeConfig;
  }
  return {
    ...bridgeConfig,
    type: normalizeBridgeType(bridgeConfig.type),
  };
};

const normalizeBridgeTypeDefinition = (bridgeTypeDef) => {
  if (!bridgeTypeDef || typeof bridgeTypeDef !== 'object') {
    return bridgeTypeDef;
  }
  return {
    ...bridgeTypeDef,
    type: normalizeBridgeType(bridgeTypeDef.type),
  };
};

const dedupeBridgeTypeDefinitions = (bridgeTypes) => {
  const byType = new Map();
  (bridgeTypes || []).forEach((item) => {
    const normalized = normalizeBridgeTypeDefinition(item);
    if (!normalized || !normalized.type) {
      return;
    }
    byType.set(normalized.type, normalized);
  });
  return Array.from(byType.values());
};

export default {
  instance,
  instanceMultipart,
  instanceApiGateway,

  // **********************************************************
  //
  // ALARM LOGS
  //
  // **********************************************************

  fetchAlarmLogs(page, pageSize, searchItems = [], order = undefined) {
    const params = {
      search_items: JSON.stringify(searchItems),
      order_by: order ? JSON.stringify(order) : undefined,
      page,
      pageSize,
    };

    return instance.get('/alarm-logs', { params, timeout: 60000 }).then((response) => {
      return response.data;
    });
  },

  async createAlarmLogsComment(logId, comment) {
    const response = await instance.post(`/alarm-logs/${logId}/comments`, { message: comment });
    trackEvent('alarmlogs', 'createComment');
    return response;
  },

  async createAlarmLogsCommentsBatch(comments) {
    const response = await instance.post('/alarm-logs/__batch__/comments', { comments: comments });
    trackEvent('alarmlogs', 'createCommentsBatch');
    return response;
  },

  fetchAlarmLogReports(context, page, pageSize, searchItems = [], order = [], skipCounts = true) {
    const params = {
      search_items: JSON.stringify(searchItems),
      order_by: JSON.stringify(order),
      page,
      pageSize,
      skip_counts: skipCounts,
    };

    return instance.get('/alarm-logs/reports', { params }).then((response) => {
      return response.data;
    });
  },

  async createAlarmReport(context, report) {
    const response = await instance.post('/alarm-logs/report', { report });
    trackEvent('alarmReports', 'createOne');
    return response;
  },

  testAlarmReportAction(context, report, actionIndex) {
    return instance
      .post('/alarm-logs/report/test-report-execution', { report: { ...report, actionIndex } })
      .then((response) => {
        return response.data;
      });
  },

  async updateAlarmReport(context, report) {
    const response = await instance.put(`/alarm-logs/report/${report.id}`, { report });
    trackEvent('alarmReports', 'updateOne');
    return response;
  },

  async deleteAlarmReport(context, reportId) {
    const response = await instance.delete(`/alarm-logs/report/${reportId}`);
    trackEvent('alarmReports', 'deleteOne');
    return response;
  },

  // **********************************************************
  //
  // USER
  //
  // **********************************************************
  getUser(context, userId) {
    context.$store.state.app.load = 10;
    context.$store.state.app.loading = true;
    return instance
      .get(`/user/${!userId ? context.$store.state.authentication.user.id : userId}`)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        context.$store.state.app.loading = false;
        context.$store.state.app.load = 100;
      });
  },

  async createApiUser(tenantId, data) {
    const response = await instance.post(`/user/apiuser/tenant/${tenantId}`, data);
    trackEvent('users', 'createApiUser');
    return response;
  },

  async createUser(tenantId, data) {
    const response = await instance.post(`/user/tenant/${tenantId}`, data);
    trackEvent('users', 'createOne');
    return response;
  },

  async updateUser(data) {
    const response = await instance.put(`/user/${data.id}`, data);
    trackEvent('users', 'updateOne');
    return response;
  },

  async updateUserPartial(data) {
    const response = await instance.patch(`/user/${data.id}`, data);
    trackEvent('users', 'updateOnePartial');
    return response;
  },

  fetchUsers(scopeIds, scopeOptions, fetchOptions, offset, limit, order, searchItem) {
    return instance
      .get('/user/', {
        params: {
          scopeIds: JSON.stringify(scopeIds),
          scopeOptions: JSON.stringify(scopeOptions),
          fetchOptions: JSON.stringify(fetchOptions),
          offset,
          limit,
          order: JSON.stringify(order),
          search_item: searchItem,
        },
        timeout: 45000,
      })
      .then((response) => {
        return response.data;
      });
  },

  async deleteUser(id) {
    const response = await instance.delete(`/user/${id}`);
    trackEvent('users', 'deleteOne');
    return response;
  },

  async resetPasswordWithToken(token, password) {
    const response = await instance.post('/user/passwordreset/update', { token, password });
    trackEvent('users', 'resetPasswordWithToken');
    return response;
  },

  getPasswordPolicyForToken(token) {
    return instance.get(`/user/passwordpolicy/${token}`).then((response) => {
      return response.data;
    });
  },

  validateUserPassword(password, tenantId, passwordToken) {
    return instance.post('/user/passwordpolicy/validate', { password, tenantId, passwordToken }).then((response) => {
      return response.data;
    });
  },

  async updatePassword(userId, password, newPassword) {
    const response = await instance.put(`/user/${userId}/password`, { password, newPassword });
    trackEvent('users', 'updatePassword');
    return response;
  },

  async resetPasswordRequest(email) {
    const response = await instance.post('/user/passwordreset', { email });
    trackEvent('users', 'resetPasswordRequest');
    return response;
  },

  async enableTwoFactorAuth(userId, token, options = {}) {
    const param = {
      domain: window.location.host,
    };
    if (token) {
      param.token = token;
    }
    if (options.enforced === true) {
      param.enforced = true;
    }
    if (options.complete === true) {
      param.complete = true;
    }
    const response = await instance.put(`/user/${userId}/2fa/enable`, param).then((res) => res.data);
    trackEvent('users', 'enableTwoFactorAuth');
    return response;
  },

  async disableTwoFactorAuth(userId, token) {
    const param = {};
    if (token) {
      param.token = token;
    }
    const response = await instance.put(`/user/${userId}/2fa/disable`, param).then((res) => res.data);
    trackEvent('users', 'disableTwoFactorAuth');
    return response;
  },

  getUserAnnouncements() {
    return instance.get('/user/own/announcements').then((response) => {
      return response.data;
    });
  },

  async userAcceptEula() {
    const response = await instance.put('/user/own/eula/', {
      accepted: true,
    });

    trackEvent('users', 'acceptEula');
    return response;
  },

  async sendFeedback(data) {
    const response = await instanceMultipart.post('/user/feedback', data, { timeout: 50000 }).then((response) => {
      return response.data;
    });
    trackEvent('users', 'sendFeedback');
    return response;
  },

  // **********************************************************
  //
  // PERMISSIONS
  //
  // **********************************************************
  async updateUserAccess(data, originalUser) {
    let rolesByScope = {};

    // check if access was deleted
    for (let i = 0; i < originalUser.access.length; i++) {
      const orgAccess = originalUser.access[i];
      if (!data.access.find((a) => a.scopeId === orgAccess.scopeId)) {
        // deleting an access works by updating it with empty roles
        await instance.put('/acl/updateaccess', {
          tenant: { id: orgAccess.tenantId },
          scope: { id: orgAccess.scopeId },
          user: { id: data.id },
          roles: [],
        });
      } else {
        if (!rolesByScope[orgAccess.scopeId]) {
          rolesByScope[orgAccess.scopeId] = [];
        }
        rolesByScope[orgAccess.scopeId] = rolesByScope[orgAccess.scopeId].concat(orgAccess.roles);
      }
    }

    // now check if any non-deleted access changed or was added
    for (let i = 0; i < data.access.length; i++) {
      const access = data.access[i];
      // first check if the number of roles changed
      let accessHasNotChanged =
        rolesByScope[access.scopeId] && rolesByScope[access.scopeId].length === access.roles.length;
      if (accessHasNotChanged) {
        // also check that all roles are still the same
        accessHasNotChanged = access.roles.every((role) => {
          return rolesByScope[access.scopeId].some((originalRole) => {
            return role.id === originalRole.id;
          });
        });
      }
      if (!accessHasNotChanged) {
        await instance.put('/acl/updateaccess', {
          tenant: { id: access.tenantId },
          scope: { id: access.scopeId },
          user: { id: data.id },
          roles: access.roles,
        });
      }
    }

    trackEvent('users', 'updateAccess');
  },

  fetchRules() {
    return instance.get('/acl/rule/').then((response) => {
      return response.data;
    });
  },

  fetchRulesByTenant(tenantId) {
    return instance.get(`/acl/rule/${tenantId}`).then((response) => {
      return response.data;
    });
  },

  fetchRoles(scopeId, userId, includeRules = false) {
    return instance.get('/acl/role/', { params: { scopeId, userId, includeRules } }).then((response) => {
      return response.data;
    });
  },

  fetchDefaultRoles(scopeId, originalScopeOnly) {
    return instance.get(`scope/roles/${scopeId}`, { params: { originalScopeOnly } }).then((response) => {
      return response.data;
    });
  },

  async updateDefaultRoles(scopeId, roleIds) {
    const response = await instance.put('scope/roles/', { scopeId, roleIds }).then((response) => {
      return response.data;
    });

    trackEvent('roles', 'updateDefaultRoles');
    return response;
  },

  async createRole(data) {
    const response = await instance.post('/acl/role/', data).then((response) => {
      return response.data;
    });
    trackEvent('roles', 'createOne');
    return response;
  },

  async updateRole(data) {
    const response = await instance.put(`/acl/role/${data.id}`, data).then((response) => {
      return response.data;
    });

    trackEvent('roles', 'updateOne');
    return response;
  },

  async deleteRole(roleId) {
    const response = await instance.delete(`/acl/role/${roleId}`).then((response) => {
      return response.data;
    });

    trackEvent('roles', 'deleteOne');
    return response;
  },

  checkAccess(subject, action, props) {
    return instance
      .get('/acl/checkaccess', {
        params: { permissions: JSON.stringify([{ subject, action }]), props: JSON.stringify(props) },
      })
      .then((response) => {
        return response.data;
      });
  },

  checkAccesses(permissions, props) {
    return instance
      .get('/acl/checkaccess', {
        params: { permissions: JSON.stringify(permissions), props: JSON.stringify(props) },
      })
      .then((response) => {
        return response.data;
      });
  },

  getScope(scopeId) {
    return instance.get(`/scope/${scopeId}/`).then((response) => {
      return response.data;
    });
  },

  fetchScopes(userId, subject, action) {
    // eslint-disable-next-line no-console
    console.log(
      'Should not be used, for it does not scale with too many scopes. Use the tree-methods as userScopesFetchSearchResult instead.'
    );
    return instance.get(`/acl/scopes/${userId}`, { params: { subject, action } }).then((response) => {
      return response.data;
    });
  },

  userScopesFetchChildren(userId, subject, action, parentScopeId, offset, limit) {
    return instance
      .get(`/acl/scopes/children/${parentScopeId ? parentScopeId : ''}`, {
        params: { user_id: userId, offset, limit, subject, action },
      })
      .then((response) => {
        return response.data;
      });
  },

  userScopesFetchSearchResult(userId, subject, action, searchItem, options, targetScopeId) {
    let optionsJson = JSON.stringify(options);
    if (!optionsJson) {
      optionsJson = 'null';
    }
    return instance
      .get('/acl/scopes/partial-tree/', {
        params: {
          user_id: userId,
          subject,
          action,
          search_item: searchItem,
          options: optionsJson,
          target_scope_id: targetScopeId,
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  // **********************************************************
  //
  // TENANT
  //
  // **********************************************************
  tenantGetById(id, fetchOptions = []) {
    return instance
      .get(`/tenant/${id}`, {
        params: {
          fetch_options: JSON.stringify(fetchOptions),
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  tenantFetchAll() {
    return instance.get('/tenant/fetch/all', { timeout: 30000 }).then((response) => {
      return response.data;
    });
  },

  async tenantUpdate(context, data) {
    const response = await instance.put(`/tenant/${data.id}`, data).then((response) => {
      // Invalidate VueX cache for n2Accounts on successful response
      context.$store.commit('app/n2AccountsInvalidateCache');
      return response;
    });

    trackEvent('tenants', 'updateOne');
    return response;
  },

  async tenantCreate(context, data) {
    const response = await instance.post('/tenant', data).then((response) => {
      // Invalidate VueX cache for n2Accounts on successful response
      context.$store.commit('app/n2AccountsInvalidateCache');
      return response;
    });

    trackEvent('tenants', 'createOne');
    return response;
  },

  async tenantDelete(context, tenantId) {
    const response = await instance.delete(`/tenant/${tenantId}`).then((response) => {
      // Invalidate VueX cache for n2Accounts on successful response
      context.$store.commit('app/n2AccountsInvalidateCache');
      return response;
    });

    trackEvent('tenants', 'deleteOne');
    return response;
  },

  async tenantUpdateRetentionPolicy(data) {
    const response = await instance.put(`/tenant/${data.id}/retentionPolicy`, data);
    trackEvent('tenants', 'updateRetentionPolicy');
    return response;
  },

  async tenantUpdateCsvExportLimit(data) {
    const response = await instance.put(`/tenant/${data.id}/csvExportLimit`, data);
    trackEvent('tenants', 'updateCsvExportLimit');
    return response;
  },

  async tenantUpdateDashboardDatapointLimit(data) {
    const response = await instance.put(`/tenant/${data.id}/dashboardDatapointLimit`, data);
    trackEvent('tenants', 'updateDashboardDatapointLimit');
    return response;
  },

  async tenantUpdateTemplatesLimit(data) {
    const response = await instance.put(`/tenant/${data.id}/template-limits`, data);
    trackEvent('tenants', 'updateTemplatesLimit');
    return response;
  },

  getTemplatesLimit(tenantId) {
    return instance.get(`/tenant/template-limits/${tenantId}`);
  },

  async tenantCreateSystemAnnouncement(tenantId, data) {
    const respone = await instance.post(`/tenant/announcement/${tenantId}`, data);
    trackEvent('tenants', 'createSystemAnnouncement');
    return respone;
  },

  async tenantUpdateSystemAnnouncement(tenantId, data) {
    const respone = await instance.put(`/tenant/announcement/${tenantId}`, data);
    trackEvent('tenants', 'updateSystemAnnouncement');
    return respone;
  },

  async tenantDeleteSystemAnnouncement(id) {
    const response = await instance.delete(`/tenant/announcement/${id}`);
    trackEvent('tenants', 'deleteSystemAnnouncement');
    return response;
  },

  tenantFetchAllSystemAnnouncement(tenantId) {
    return instance.get(`/tenant/announcement/${tenantId}`).then((response) => {
      return response.data;
    });
  },

  async tenantUpdateSystemAnnouncementState(tenantId, data) {
    const response = await instance.put(`/tenant/announcement/state/${tenantId}`, data);
    trackEvent('tenants', 'updateSystemAnnouncementState');
    return response;
  },

  tenantFetchModules(tenantId) {
    return instance
      .get(`/acl/modules/tenant/${tenantId || ''}`)
      .catch((error) => {
        console.error(error);
        // TODO: show snackbar to user
        return { data: [] };
      })
      .then((response) => {
        return response.data;
      });
  },

  async tenantToggleModule(tenantId, moduleKey, isEnabled, modulesSettings) {
    const response = await instance.post(`/acl/modules/tenant/${tenantId}/toggle/${moduleKey}`, {
      isEnabled,
      modulesSettings,
    });
    trackEvent('tenants', 'toggleModule');
    return response;
  },

  tenantGetPasswordPolicy(tenantId) {
    return instance.get(`/tenant/passwordpolicy/tenant/${tenantId ? tenantId : ''}`).then((response) => {
      return response.data;
    });
  },

  tenantGetServiceBuilderParams(tenantId) {
    return instance.get(`/tenant/${tenantId}/service-builder-params`).then((response) => {
      return response.data;
    });
  },

  tenantGetServiceBuilders(offset, limit, sortBy, sortDesc) {
    return instance
      .get('/tenant/service-builders/list', {
        params: {
          offset,
          limit,
          sortBy,
          sortDesc,
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  // **********************************************************
  //
  // BRIDGES
  //
  // **********************************************************
  bridgesGetPlainConfigs(type) {
    return instance.get(`/bridge/configs/plain/${type}`).then((response) => {
      return response.data;
    });
  },

  getBridgeTypes() {
    return instance.get('/bridge/types').then((response) => {
      return dedupeBridgeTypeDefinitions(response.data);
    });
  },

  getBridgesForTenant(tenantId) {
    return instance.get(`/bridge/configs/tenant/${tenantId}`).then((response) => {
      return (response.data || []).map(normalizeBridgeConfig);
    });
  },

  getBridgesForTenants(tenantIds) {
    return instance.get(`/bridge/configs?tenantIds=[${tenantIds}]`).then((response) => {
      return (response.data || []).map((entry) => {
        if (!entry || typeof entry !== 'object') {
          return entry;
        }
        return {
          ...entry,
          bridge: normalizeBridgeConfig(entry.bridge),
        };
      });
    });
  },

  getBridgesForTenantsPaginated(
    tenantIds,
    page = 1,
    pageSize = 10,
    includeTotal = true,
    search = '',
    sortBy = 'id',
    sortDesc = false
  ) {
    const params = new URLSearchParams({
      tenantIds: `[${tenantIds}]`,
      page: page.toString(),
      pageSize: pageSize.toString(),
      includeTotal: includeTotal.toString(),
    });

    if (search && search.trim()) {
      params.append('search', search.trim());
    }

    if (sortBy) {
      params.append('sortBy', sortBy);
      params.append('sortDesc', sortDesc.toString());
    }

    return instance.get(`/bridge/configs?${params.toString()}`).then((response) => {
      const payload = response.data || {};
      if (!Array.isArray(payload.data)) {
        return payload;
      }
      return {
        ...payload,
        data: payload.data.map((entry) => {
          if (!entry || typeof entry !== 'object') {
            return entry;
          }
          return {
            ...entry,
            bridge: normalizeBridgeConfig(entry.bridge),
          };
        }),
      };
    });
  },

  async getAllBridgesForTenants(tenantIds, pageSize = 10) {
    const allBridges = [];
    let currentPage = 1;
    let hasNextPage = true;

    while (hasNextPage) {
      const response = await this.getBridgesForTenantsPaginated(tenantIds, currentPage, pageSize, true);

      allBridges.push(...response.data);

      hasNextPage = response.pagination.hasNextPage;
      currentPage++;

      if (hasNextPage) {
        await new Promise((resolve) => setTimeout(resolve, 10));
      }
    }

    return allBridges;
  },

  getBridgesWithErrorsForTenantHierarchy(tenantId) {
    return instance.get(`/bridge/configs/tenant-hierarchy/${tenantId}/errors`).then((response) => {
      return response.data;
    });
  },

  validateBridge(type, id, parameters) {
    const bridgeIdParam = id && !isNaN(parseInt(id)) ? `bridge_id=${parseInt(id)}` : '';
    return instance.post(`/bridge/validate/${type}?${bridgeIdParam}`, parameters).then((response) => {
      return response.data;
    });
  },

  staticBridgeCommand(type, command, parameters) {
    return instance.post(`/bridge/staticCommand/${type}/${command}`, parameters).then((response) => {
      return response.data;
    });
  },

  bridgeCommand(bridgeId, command, parameters) {
    return instance.post(`/bridge/command/${bridgeId}/${command}`, parameters).then((response) => {
      return response.data;
    });
  },

  async createBridge(tenantId, type, properties) {
    const response = await instance.post(`/bridge/tenant/${tenantId}`, { type, properties }).then((response) => {
      return response.data;
    });

    trackEvent('bridges', 'createOne');
    return response;
  },

  async updateBridge(id, properties) {
    const response = await instance.put(`/bridge/${id}`, { properties }).then((response) => {
      return response.data;
    });
    trackEvent('bridges', 'updateOne');
    return response;
  },

  async resetBridgeState(id, state = 'waiting') {
    const response = await instance.post(`/bridge/${id}/state-reset`, { state }).then((response) => {
      return response.data;
    });
    trackEvent('bridges', 'resetState');
    return response;
  },

  async deleteBridge(bridgeId) {
    const response = await instance.delete(`/bridge/${bridgeId}`).then((response) => {
      return response.data;
    });
    trackEvent('bridges', 'deleteOne');
    return response;
  },

  getBridgeTraffic(bridgeId, includePayload = false, limit = 100) {
    return instance
      .get(`/bridge/traffic/${bridgeId}?limit=${limit}&payload=${includePayload}`, { timeout: 30000 })
      .then((response) => {
        return response.data;
      });
  },

  getBridgeLogs(bridgeId, limit = 100) {
    return instance.get(`/bridge/logs/${bridgeId}?limit=${limit}`, { timeout: 30000 }).then((response) => {
      return response.data;
    });
  },

  bridgeGetDependencies(bridgeId, limit) {
    return instance.get(`/bridge/${bridgeId}/dependencies?limit=${limit}`).then((response) => {
      return response.data;
    });
  },

  // **********************************************************
  //
  // BRIDGE MONITORING
  //
  // **********************************************************

  // Monitoring Config APIs
  async getBridgeMonitoringConfig(bridgeId) {
    const response = await instance.get(`/bridge/monitoring/bridge/${bridgeId}/config`);
    return response.data;
  },

  async enableBridgeMonitoring(bridgeId, data) {
    const response = await instance.post(`/bridge/monitoring/bridge/${bridgeId}/enable`, data);
    trackEvent('bridge-monitoring', 'enable');
    return response.data;
  },

  async disableBridgeMonitoring(bridgeId) {
    const response = await instance.post(`/bridge/monitoring/bridge/${bridgeId}/disable`);
    trackEvent('bridge-monitoring', 'disable');
    return response.data;
  },

  async deleteBridgeMonitoring(bridgeId) {
    const response = await instance.delete(`/bridge/monitoring/bridge/${bridgeId}`);
    trackEvent('bridge-monitoring', 'delete');
    return response.data;
  },

  async updateMonitoringConfig(configId, data) {
    const response = await instance.put(`/bridge/monitoring/configs/${configId}`, data);
    trackEvent('bridge-monitoring', 'update-config');
    return response.data;
  },

  // Monitoring Rules APIs
  async listMonitoringRules(params) {
    const response = await instance.get('/bridge/monitoring/rules', { params });
    return response.data;
  },

  async createMonitoringRule(data) {
    const response = await instance.post('/bridge/monitoring/rules', data);
    trackEvent('bridge-monitoring-rules', 'create');
    return response.data;
  },

  async updateMonitoringRule(ruleId, data) {
    const response = await instance.put(`/bridge/monitoring/rules/${ruleId}`, data);
    trackEvent('bridge-monitoring-rules', 'update');
    return response.data;
  },

  async deleteMonitoringRule(ruleId) {
    const response = await instance.delete(`/bridge/monitoring/rules/${ruleId}`);
    trackEvent('bridge-monitoring-rules', 'delete');
    return response.data;
  },

  async toggleMonitoringRule(ruleId, enabled, silenced) {
    const payload = {};
    if (enabled !== undefined) {
      payload.enabled = enabled;
    }
    if (silenced !== undefined) {
      payload.silenced = silenced;
    }
    const response = await instance.patch(`/bridge/monitoring/rules/${ruleId}/toggle`, payload);
    trackEvent('bridge-monitoring-rules', 'toggle');
    return response.data;
  },

  async resolveMonitoringRule(ruleId, userId, reason) {
    const response = await instance.post(`/bridge/monitoring/rule/${ruleId}/resolve`, {
      userId,
      reason,
    });
    trackEvent('bridge-monitoring-rules', 'resolve');
    return response.data;
  },

  async getRuleHistory(ruleId, limit = 100) {
    const response = await instance.get(`/bridge/monitoring/rule/${ruleId}/history`, {
      params: { limit },
    });
    return response.data;
  },

  // Metrics APIs
  async getBridgePacketMetrics(bridgeId, params) {
    const response = await instance.get(`/bridge/monitoring/${bridgeId}/metrics/packets`, { params });
    return response.data;
  },

  async getBridgeStateMetrics(bridgeId, params) {
    const response = await instance.get(`/bridge/monitoring/${bridgeId}/metrics/states`, { params });
    return response.data;
  },

  ...consumers,

  // **********************************************************
  //
  // VERSION DRIFT
  //
  // **********************************************************
  getVersionDriftAccountIntegrations(accountId) {
    return instance.get(`/versiondrift/account-integrations/${accountId}`).then((response) => {
      return response.data;
    });
  },

  async enableVersionDriftAccountIntegration(accountId, type) {
    const response = await instance
      .get(`/versiondrift/account-integrations/${accountId}/enable/${type}`)
      .then((response) => {
        return response.data;
      });
    trackEvent('versiondrift', 'enableIntegration');
    return response;
  },

  // **********************************************************
  //
  // DIGITAL TWIN & VIRTUAL DEVICE
  //
  // **********************************************************
  ...digitalTwin,
  ...virtualDevice,
  virtualDeviceDrivers,
  dashboards,
  digitalTwinTemplates,

  // **********************************************************
  //
  // FAVORITES
  //
  // **********************************************************
  fetchFavoritesByUserId(id) {
    return instance.get(`/favorite/user/${id}`).then((response) => {
      return response.data;
    });
  },

  getFavoritesByObjectIdUserId(objectID, userId, type) {
    return instance.get(`/favorite/object/${objectID}/user/${userId}/type/${type}`).then((response) => {
      return response.data;
    });
  },

  async createFavorite(data) {
    const response = await instance.post('/favorite', data);
    trackEvent('favorites', 'createOne');
    return response;
  },

  async deleteFavorite(id) {
    const response = await instance.delete(`/favorite/${id}`);
    trackEvent('favorites', 'deleteOne');
    return response;
  },

  // **********************************************************
  //
  // MODULES
  //
  // **********************************************************

  ...isAssetMgmt,
  ...isFm,

  // **********************************************************
  //
  // File Upload
  //
  // **********************************************************
  async saveFile(context, data) {
    const response = await instanceMultipart.post('/upload/post', data).then((response) => {
      return response.data;
    });
    trackEvent('upload', 'saveFile');
    return response;
  },

  getFile(id) {
    return instance.get(`/upload/${id}`).then((response) => {
      return response.data;
    });
  },

  saveAttachment(context, data, onProgressHandler, apiVersion = 1) {
    switch (apiVersion) {
      case 1:
        return this.saveAtachmentV1(context, data, onProgressHandler);

      case 2:
        return this.saveAtachmentV2(context, data, onProgressHandler);

      default:
        throw new Error('SaveAttachment for version ${apiVersion} is not yet implemented.');
    }
  },

  async saveAtachmentV1(context, data, onProgressHandler) {
    const response = await instanceMultipart
      .post('/upload/post', data, {
        onUploadProgress: onProgressHandler,
        timeout: 50000,
      })
      .then((response) => {
        return response.data;
      });

    trackEvent('upload', 'saveAtachmentV1');
    return response;
  },

  async saveAtachmentV2(context, data, onProgressHandler) {
    const response = await instanceMultipart.post('/upload/v2/post', data, {
      onUploadProgress: onProgressHandler,
      timeout: 50000,
    });

    trackEvent('upload', 'saveAtachmentV2');

    return response.data;
  },

  // **********************************************************
  //
  // Dispatcher
  //
  // **********************************************************

  async deviceDriverParsePayload(deviceDriverOrDriverId, payload) {
    const parser = typeof deviceDriverOrDriverId === 'object' ? deviceDriverOrDriverId : undefined;
    const deviceDriverId = typeof deviceDriverOrDriverId === 'object' ? undefined : deviceDriverOrDriverId;

    const response = await instance.post('/dispatcher/devicedriver/parse', { deviceDriverId, parser, payload });
    return response.data;
  },

  // **********************************************************
  //
  // Generic API calls to create, set and get information from
  // a digital twins `data` key
  //
  // **********************************************************
  getTwinData(context, id, key) {
    return instance
      .get(`/digitaltwin/${id}`, {
        params: {
          bare: false,
          no_descendents: true,
        },
      })
      .then((response) => {
        return key ? response.data.data[key] : response.data;
      });
  },

  async setTwinData(context, id, key, data) {
    const response = await instance
      .get(`/digitaltwin/${id}`, {
        params: {
          bare: false,
          no_descendents: true,
        },
      })
      .then((response) => {
        const twinObject = response.data;
        twinObject.data[key] = data;

        return instance.put(`/digitaltwin/${id}/`, twinObject).then((response) => {
          return response.data.data[key];
        });
      });

    trackEvent('digitaltwins', 'setTwinData');
    return response;
  },

  // **********************************************************
  //
  // Email Templates
  //
  // **********************************************************
  getAllEmailTemplates(id) {
    return instance.get(`/mail/templates/?tenantId=${id}`).then((res) => res.data);
  },

  getEmailTemplate(id, identifier) {
    return instance.get(`/mail/template/${identifier}?tenantId=${id}`).then((res) => res.data);
  },

  async setEmailTemplate(id, identifier, data) {
    const response = await instance.post(`/mail/template/${identifier}?tenantId=${id}`, data).then((response) => {
      return response.data;
    });

    trackEvent('mail', 'setEmailTemplate');
    return response;
  },

  renderEmailTemplate(subject, body, templateLocals) {
    return instance.post('/mail/template/render', { subject, body, templateLocals }).then((response) => {
      return response.data;
    });
  },

  // **********************************************************
  //
  // Activity Logs (System Logs)
  //
  // **********************************************************
  fetchActivityLogs(context, data) {
    return instance.post('/activityLog', data).then((response) => {
      return response.data;
    });
  },

  async downloadActivityLogsCSVExport(context, data) {
    const response = await instance
      .post('/activityLog/csv-export', data, {
        responseType: 'stream',
        timeout: 210000,
      })
      .then((response) => {
        return response.data;
      });

    trackEvent('activityLogs', 'downloadCSVExport');
    return response;
  },

  // **********************************************************
  //
  // Niota
  //
  // **********************************************************
  fetchNiotaDevices(bridgeId, searchItem) {
    return instance.post(`/niota/devices/${searchItem}`, { bridgeId }).then((response) => {
      return response.data;
    });
  },

  getNiotaDevice(bridgeId, deviceId) {
    return instance.post(`/niota/device/${deviceId}`, { bridgeId }).then((response) => {
      return response.data;
    });
  },

  fetchNiotaDeviceVariables(bridgeId, deviceId) {
    // { randoVariable:
    //   { time: '2019-11-27T12:00:14.000Z',
    //       application_id: '9',
    //       device_eui: '000064FFFEA3887D',
    //       device_id: '1716',
    //       device_name: 'Sushi Vibration Sensor 01',
    //       device_tags: null,
    //       device_type_id: '492',
    //       device_type_name: 'Sushi Vibration Sensor HZ',
    //       orig_value: 19996,
    //       value: 19996,
    //       value_type_name: '°C',
    //       variable: 'randoVariable'
    //   }
    // }
    return instance.post(`/niota/deviceVariables/${deviceId}`, { bridgeId }).then((response) => {
      return response.data;
    });
  },

  // **********************************************************
  //
  // BACKGROUND JOBS
  //
  // **********************************************************
  async createBackgroundJob(data) {
    const response = await instance.post('/background-jobs', data);
    trackEvent('backgroundJobs', 'createOne');
    return response;
  },

  async deleteBackgroundJob(id) {
    const response = await instance.delete(`/background-jobs/${id}`);
    trackEvent('backgroundJobs', 'deleteOne');
    return response;
  },

  async cancelBackgroundJob(id) {
    const response = await instance.get(`/background-jobs/${id}/cancel`);
    trackEvent('backgroundJobs', 'cancelOne');
    return response;
  },

  fetchBackgroundJob(id) {
    return instance.get(`/background-jobs/${id}`).then((response) => {
      return response.data;
    });
  },

  fetchBackgroundJobs(offset, limit, sortItem, sortDesc) {
    return instance
      .get('/background-jobs', {
        params: {
          offset,
          limit,
          sortItem,
          sortDesc: JSON.stringify(sortDesc),
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  fetchBackgroundJobByAccountIds(ids, sortBy, sortDesc, limit, offset, type) {
    return instance
      .get(
        `/background-runners?tenantIds=${JSON.stringify(
          ids
        )} &sortBy=${sortBy}&sortDesc=${sortDesc}&limit=${limit}&offset=${offset}&type=${type}`
      )
      .then((response) => {
        return response.data;
      });
  },

  // **********************************************************
  //
  // BACKGROUND RUNNERS
  //
  // **********************************************************
  async createBackgroundRunnerSingleExportDeviceStates(data) {
    const response = await instance.post('/background-runners/device-states-single-export', data);
    trackEvent('backgroundRunners', 'createSingleExportDeviceStates');
    return response;
  },

  async createBackgroundRunnerBulkExportDeviceStates(data) {
    const response = await instance.post('/background-runners/device-states-bulk-export', data);
    trackEvent('backgroundRunners', 'createBulkExportDeviceStates');
    return response;
  },

  async createBackgroundRunnerBulkEdit(data) {
    const response = await instance.post('/background-runners/virtualdevices-bulk-edit', data);
    trackEvent('backgroundRunners', 'createBulkVDEdit');
    return response;
  },

  async createBackgroundRunnerBulkImport(data) {
    const response = await instance.post('/background-runners/virtualdevices-bulk-import', data);
    trackEvent('backgroundRunners', 'createBulkVDImport');
    return response;
  },

  async deleteBackgroundRunner(id) {
    const response = await instance.delete(`/background-runners/${id}`);
    trackEvent('backgroundRunners', 'deleteOne');
    return response;
  },

  async cancelBackgroundRunner(id) {
    const response = await instance.post(`/background-runners/${id}/cancel`);
    trackEvent('backgroundRunners', 'cancelOne');
    return response;
  },

  async retryBackgroundRunnerTask(bgRunnerId, taskId) {
    const response = await instance.post(`/background-runners/${bgRunnerId}/retry-task/${taskId}/`);
    trackEvent('backgroundRunners', 'retryTask');
    return response;
  },

  fetchBackgroundRunner(id) {
    return instance.get(`/background-runners/${id}`).then((response) => {
      return response.data;
    });
  },

  fetchBackgroundRunnersByTenant(id, offset, limit, sortItem, sortDesc) {
    return instance
      .get('/background-runners', {
        params: {
          id,
          offset,
          limit,
          sortItem,
          sortDesc: JSON.stringify(sortDesc),
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  fetchBackgroundRunnerTasksLogs(id, offset, limit, sortBy, sortDesc) {
    return instance
      .get(`/background-runners/${id}/tasks`, {
        params: {
          offset,
          limit,
          sortBy,
          sortDesc,
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  precalculationBackgroundRunner(data) {
    return instance.post('/virtualdevice/background-runners/precalculation', data);
  },

  // **********************************************************
  //
  // JOB DEFINITIONS
  //
  // **********************************************************

  fetchJobDefinitions(tenantIds, offset, limit, sortBy, sortDesc) {
    return instance
      .get('/job-definitions', {
        params: {
          tenantIds: JSON.stringify(tenantIds),
          offset,
          limit,
          sortBy,
          sortDesc,
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  async createJobDefinition(data) {
    const response = await instance.post('/job-definitions', data);
    trackEvent('jobDefinitions', 'createOne');
    return response;
  },

  async deleteJobDefinition(jobId) {
    const response = await instance.delete(`/job-definitions/${jobId}`);
    trackEvent('jobDefinitions', 'deleteOne');
    return response;
  },

  async runJobDefinition(jobId) {
    const response = await instance.post(`/job-definitions/${jobId}/run`);
    trackEvent('jobDefinitions', 'runOne');
    return response;
  },

  async updateJobDefinition(jobId, active) {
    const response = await instance.patch(`/job-definitions/${jobId}`, { active: active });
    trackEvent('jobDefinitions', 'updateOne');
    return response;
  },

  // **********************************************************
  //
  // UTILS
  //
  // **********************************************************
  checkAddress(address) {
    return instance.get(`/utils/geocode/getcoordinates/${encodeURIComponent(address)}`).then((response) => {
      return response.data;
    });
  },
  geoCodeCoordinates(lat, lng) {
    return instance
      .get('/utils/geocode/getaddress/', {
        params: {
          lat,
          lng,
        },
      })
      .then((response) => {
        return response.data;
      });
  },

  getTheming(domain, tenantId) {
    let queryParam = '';
    if (tenantId) {
      queryParam += `tenantId=${tenantId}&`;
    }
    if (domain) {
      queryParam += `domain=${domain}`;
    }
    return instance.get(`/theming/?${queryParam}`).then((response) => {
      return response.data;
    });
  },

  getTermsAndConditions(tenantId) {
    return instance.get(`/theming/terms-and-conditions/${tenantId}`).then((response) => {
      return response.data;
    });
  },

  async execConsoleAction(path, params) {
    const response = await instance
      .post('/system/console', { callPath: path, params }, { timeout: 90000 })
      .then((response) => {
        return response.data;
      });

    trackEvent('system', 'execConsoleAction');
    return response;
  },

  fetchModuleRules() {
    return instance.get('/system/modules/rules').then((response) => {
      return response.data;
    });
  },

  // **********************************************************
  //
  // Gateway Management
  //
  // **********************************************************

  gwMgmtFetch(accountId, offset, limit, order, options, searchItem, includeSubAccounts) {
    return instance
      .get('/gw/management', {
        params: {
          account_id: accountId,
          offset,
          limit,
          order: JSON.stringify(order),
          options: JSON.stringify(options),
          search_item: searchItem,
          include_sub_accounts: includeSubAccounts,
        },
        timeout: 50000,
      })
      .then((res) => res.data);
  },

  gwMgmtGetDetails(id) {
    return instance
      .get(`/gw/management/${id}/details`, {
        timeout: 30000,
      })
      .then((response) => {
        return response.data;
      });
  },

  async gwMgmtCreate(data) {
    const response = await instance.post('/gw/management', data).then((response) => {
      return response.data;
    });
    trackEvent('gateways', 'createOne');
    return response;
  },

  async gwMgmtUpdate(data) {
    const response = await instance.put(`/gw/management/${data.id}`, data).then((response) => {
      return response.data;
    });

    trackEvent('gateways', 'updateOne');
    return response;
  },

  async gwMgmtDelete(id) {
    const response = await instance.delete(`/gw/management/${id}`);
    trackEvent('gateways', 'deleteOne');
    return response;
  },

  gwMgmtLoadAllMetrics(id, data) {
    return instance
      .get(`/gw/management/${id}/metrics`, {
        params: data,
      })
      .then((res) => res.data);
  },

  gwMgmtFetchGeoData(accountId, geoBounds, options) {
    let geoBoundsJson = JSON.stringify(geoBounds);
    if (!geoBoundsJson) {
      geoBoundsJson = 'null';
    }
    let optionsJson = JSON.stringify(options);
    if (!optionsJson) {
      optionsJson = 'null';
    }
    return instance
      .get('/gw/management/geo-clusters', {
        params: {
          accountId: accountId,
          geo_bounds: geoBoundsJson,
          options: optionsJson,
        },
        timeout: 50000,
      })
      .then((res) => res.data);
  },

  gwMgmtLoadAllGatewayMetrics(data) {
    return instance
      .get('/gw/management/metrics', {
        params: data,
      })
      .then((res) => res.data);
  },

  gwMgmtFindAlertConfigs() {
    return instance.get('/gw/management/alert-configs').then((res) => res.data);
  },

  async gwMgmtUpdateAlertConfigs(data) {
    const response = await instance.post('/gw/management/alert-configs', data).then((res) => res.data);
    trackEvent('gateways', 'updateAlertConfigs');
    return response;
  },

  // **********************************************************
  //
  // OAuth2
  //
  // **********************************************************

  getOAuthClientsForURI(host, origin, oAuthClients) {
    let uri = `/theming/oauth?host=${host}&origin=${origin}`;
    if (oAuthClients) {
      uri += `&oAuthClients=${oAuthClients}`;
    }
    return instance.get(uri).then((response) => {
      return response.data;
    });
  },
  getOauthClientsForTenant(tenantId) {
    return instance.get(`/oauth2-client/tenant/${tenantId}`).then((response) => {
      return response.data;
    });
  },
  async createOauthClient(tenantId, data) {
    const response = await instance.post(`/oauth2-client/tenant/${tenantId}`, data).then((response) => {
      return response.data;
    });
    trackEvent('oauth', 'createClient');
    return response;
  },
  async updateOauthClient(clientId, data) {
    const response = await instance.put(`/oauth2-client/${clientId}`, data).then((response) => {
      return response.data;
    });
    trackEvent('oauth', 'updateClient');
    return response;
  },
  async deleteOauthClient(clientId) {
    const response = await instance.delete(`/oauth2-client/${clientId}`).then((response) => {
      return response.data;
    });
    trackEvent('oauth', 'deleteClient');
    return response;
  },
  validateOauthClient(data) {
    return instance.post('/oauth2-client/validate', data).then((response) => {
      return response.data;
    });
  },

  // **********************************************************
  //
  // OIDC Clients
  //
  // **********************************************************

  getOIDCClientsForURI(host, origin, oidcClients) {
    let uri = `/theming/oidc?host=${host}&origin=${origin}`;
    if (oidcClients) {
      uri += `&oidcClients=${oidcClients}`;
    }
    return instance.get(uri).then((response) => {
      return response.data;
    });
  },
  getOIDCClientsForTenant(tenantId) {
    return instance.get(`/oidc-client/tenant/${tenantId}`).then((response) => {
      return response.data;
    });
  },
  createOIDCClient(tenantId, data) {
    return instance.post(`/oidc-client/tenant/${tenantId}`, data).then((response) => {
      return response.data;
    });
  },
  async updateOIDCClient(clientId, data) {
    const response = await instance.put(`/oidc-client/${clientId}`, data).then((response) => {
      return response.data;
    });

    trackEvent('oidc', 'updateClient');
    return response;
  },
  async deleteOIDCClient(clientId) {
    const response = await instance.delete(`/oidc-client/${clientId}`).then((response) => {
      return response.data;
    });
    trackEvent('oidc', 'deleteClient');
    return response;
  },
  validateOIDCClient(data) {
    return instance.post('/oidc-client/validate', data).then((response) => {
      return response.data;
    });
  },

  async getFileAsBlob(id, apiVersion = 2) {
    if (!id) {
      throw new Error('Invalid parameters for getFileAsBlob, id is required');
    }

    const downloadUrl = apiVersion === 1 ? `/upload/${id}` : `/upload/v${apiVersion}/${id}`;
    const downloadTimeoutMs = 5 * 60 * 1000;
    const response = await instance.get(downloadUrl, {
      responseType: 'blob',
      timeout: downloadTimeoutMs,
    });

    return new Blob([response.data]);
  },

  async downloadFile(id, targetFileName, apiVersion) {
    if (!targetFileName) {
      throw new Error('Invalid parameters for downloadFile, id and targetFileName are required');
    }

    const blob = await this.getFileAsBlob(id, apiVersion);

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = targetFileName;
    a.click();
    window.URL.revokeObjectURL(url);
  },
};
